// src/pages/SitemapPage.tsx
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import PageHeader from '@/components/PageHeader';
import { services } from '@/data/services';
import { cn } from '@/lib/utils';

const SitemapPage = () => {
  // Define main routes for the sitemap - keep these updated with the routes in App.tsx
  const mainRoutes = [
    { url: '/', label: 'Home', description: 'Homepage of Hoptira' },
    {
      url: '/about',
      label: 'About Us',
      description: 'Learn more about Hoptira and our mission',
    },
    {
      url: '/services',
      label: 'Services',
      description: 'Explore our services and solutions',
    },
    {
      url: '/contact',
      label: 'Contact',
      description: 'Get in touch with our team',
    },
    {
      url: '/privacy-policy',
      label: 'Privacy Policy',
      description: 'Our privacy practices and policies',
    },
    {
      url: '/terms',
      label: 'Terms & Conditions',
      description: 'Terms and conditions for using our services',
    },
    {
      url: '/sitemap',
      label: 'Sitemap',
      description: 'Overview of all pages on our site',
    },
  ];

  // Define service detail routes - automatically generated from services data
  const serviceRoutes = services.map((service) => ({
    url: `/services?tab=${service.id}`,
    label: `${service.title} Services`,
    description: `Learn more about our ${service.title.toLowerCase()} services and solutions`,
  }));

  return (
    <div>
      <PageHeader
        title="Sitemap"
        description="Find all the pages available on our website"
      />

      <div className="container mx-auto px-4 pt-16 pb-48 bg-white">
        <motion.div
          className="max-w-full mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}>
          <section className="mb-12">
            <h2 className="text-3xl font-bold mb-6">Main Pages</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {mainRoutes.map((route, index) => (
                <SitemapLink
                  key={index}
                  url={route.url}
                  label={route.label}
                  description={route.description}
                />
              ))}
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6">Service Pages</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {serviceRoutes.map((route, index) => (
                <SitemapLink
                  key={index}
                  url={route.url}
                  label={route.label}
                  description={route.description}
                />
              ))}
            </div>
          </section>
        </motion.div>
      </div>
    </div>
  );
};

interface SitemapLinkProps {
  url: string;
  label: string;
  description: string;
}

const SitemapLink = ({ url, label, description }: SitemapLinkProps) => {
  return (
    <motion.div
      className={cn(
        'p-6 border border-gray-200 rounded-lg',
        'hover:border-secondary hover:shadow-md transition-all',
      )}
      whileHover={{ y: -4 }}
      transition={{ duration: 0.2 }}>
      <Link to={url} className="block">
        <h3 className="text-xl font-semibold text-primary hover:text-secondary transition-colors mb-2">
          {label}
        </h3>
        <p className="text-gray-600">{description}</p>
      </Link>
    </motion.div>
  );
};

export default SitemapPage;
